import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { completePhoneConnection } from '@biasafe.queries/phone-callback/phone-callback';
import { verifyPhoneConsent } from '@biasafe.queries/phone-verify/phone-verify';
import { requestPhoneAdminConsent, requestPhoneConsent } from './connectFlows';
import { invalidatePhoneStatus } from './phoneQueries';
import { extractErrorMessage } from '@/shared/utils/apiErrors';

/**
 * The Teams Phone connect action: Microsoft sign-in, then (when the tenant has
 * not approved the app yet) the admin-consent page, then the server's re-check.
 *
 * EXTRACTED FROM `PhoneConnectCard` so the Integrations dialog and the "Connect
 * your tools" step press the same button. Who may connect is NOT decided here:
 * the server refuses a non-admin at `begin`, and refuses a Microsoft sign-in
 * that does not carry a tenant administrator role (the id_token `wids` check in
 * `phone/identity.py`) at `complete`. Callers decide whether to DRAW the button
 * from `canManage`; this only runs the flow and reports how it ended.
 *
 * `connect(tenantHint?)` takes the optional, already-checked tenant id or domain
 * for a single-tenant app (`tenantHint.ts`); callers with none pass nothing.
 *
 * `connect` never throws. It resolves to 'connected', 'pending' (consent was
 * recorded and Microsoft has not answered the first check yet) or 'failed',
 * with `error` holding a sentence for a person.
 */
export type PhoneConnectOutcome = 'connected' | 'pending' | 'failed';

export function usePhoneConnect() {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = async (tenantHint?: string): Promise<PhoneConnectOutcome> => {
    setBusy(true);
    setError(null);
    try {
      const reply = await requestPhoneConsent(tenantHint);
      let status = await completePhoneConnection({
        state: reply.state,
        idToken: reply.id_token ?? null,
        tenant: reply.tenant ?? null,
        adminConsent: reply.admin_consent ?? null,
        error: reply.error ?? null,
      });
      if (status.consentUrl) {
        // Signed in, tenant proven, but the app has no approval there yet: send the
        // admin to approve it, then have the server re-check the tenant it proved.
        await requestPhoneAdminConsent(status.consentUrl);
        // The consent state ties this approval to the sign-in the server proved.
        const consentState =
          new URL(status.consentUrl).searchParams.get('state') ?? '';
        status = await verifyPhoneConsent({ state: consentState });
      }
      await invalidatePhoneStatus(queryClient);
      if (status.connection?.status === 'CONNECTED') {
        toast.success('Microsoft Teams Phone connected');
        return 'connected';
      }
      // Consent was recorded and Microsoft has not answered the first check
      // yet (a new approval can take a few seconds to take effect).
      toast.info(
        'Approval recorded. Microsoft has not answered yet, so the card will show the state until it does.',
      );
      return 'pending';
    } catch (err) {
      const message = extractErrorMessage(
        err,
        err instanceof Error ? err.message : 'Could not connect Teams Phone.',
      );
      setError(message);
      toast.error(message);
      // A declined consent is written to the row; show what it now says.
      void invalidatePhoneStatus(queryClient);
      return 'failed';
    } finally {
      setBusy(false);
    }
  };

  return { connect, busy, error };
}
